import { FlaskConical, ClipboardCheck, UserCheck, ArrowRight } from "lucide-react";
import { FadeIn, SectionHeader } from "../components/Motion";
import { scrollToId } from "../lib/scroll";

const STEPS = ["Formulation review", "Storage & distribution conditions", "Estimate + rationale", "Limitations if any"];

const Solution = () => (
  <section id="solution" data-testid="solution-section" className="relative py-28 sm:py-36">
    <div className="pointer-events-none absolute left-0 top-1/3 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[130px]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeader
        index="02"
        label="The Solution"
        title="A food scientist between you and the laboratory."
        lead="Shelf Sense sits between your business and the lab. Instead of spending thousands on lab testing before you even know if your product is commercially viable, you get a science-backed shelf-life estimate first — based on your product's formulation, packaging, and storage conditions."
      />

      <div className="mt-20 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        <FadeIn className="lg:row-span-2">
          <div className="group relative h-full overflow-hidden rounded-[2rem]" data-testid="solution-cell-image">
            <img
              src="https://images.unsplash.com/photo-1686820740642-5a1fcd0300a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxnbGFzcyUyMHN0b3JhZ2UlMjBqYXJzJTIwZ3JhaW5zJTIwd29vZGVuJTIwbGlkfGVufDB8fHx8MTc5MzY3MDM4MXww&ixlib=rb-4.1.0&q=85"
              alt="Unbranded glass jars of dry food ingredients reviewed for shelf-life estimation"
              className="h-full min-h-[320px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0812] via-transparent to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 font-mono text-xs uppercase tracking-[0.2em] text-green-300">
              Real food science, applied to your product
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="glass spotlight-frame h-full rounded-[2rem] p-8" data-testid="solution-cell-models">
            <FlaskConical className="h-8 w-8 text-green-400" />
            <h3 className="mt-5 font-display text-xl font-semibold text-slate-50">Established food-science models</h3>
            <p className="mt-3 text-base leading-relaxed text-slate-400">
              Every Shelf Sense report is grounded in established models and peer-reviewed literature, applied before committing to a lab study.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.18}>
          <div className="glass spotlight-frame h-full rounded-[2rem] p-8" data-testid="solution-cell-process">
            <ClipboardCheck className="h-8 w-8 text-green-400" />
            <h3 className="mt-5 font-display text-xl font-semibold text-slate-50">A documented, repeatable process</h3>
            <p className="mt-3 text-base leading-relaxed text-slate-400">
              Estimates follow a documented workflow — formulation review, storage and distribution conditions, then an estimate with its full rationale in writing.
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-slate-500">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="font-medium text-purple-400">0{i + 1}</span>
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="text-slate-300">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.26} className="md:col-span-2 lg:col-span-2">
          <div className="glass spotlight-frame flex h-full flex-col justify-between gap-8 rounded-[2rem] p-8 sm:flex-row sm:items-center" data-testid="solution-cell-scientist">
            <div className="max-w-xl">
              <UserCheck className="h-8 w-8 text-green-400" />
              <h3 className="mt-5 font-display text-xl font-semibold text-slate-50">Delivered by a qualified food scientist</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-400">
                No black-box software output. Every estimate is reviewed and explained by a qualified food scientist you can talk to.
              </p>
            </div>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToId("contact"); }}
              data-testid="solution-cta-contact"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-green-400/40 px-6 py-3 text-sm font-semibold text-green-300 transition-colors duration-300 hover:bg-green-400/10"
            >
              Ask about your product
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

export default Solution;
